// https://github.com/paidy/api-consumers-rust/blob/master/src/ops/email_reminder_flow.rs

import { parsePhoneNumber } from 'libphonenumber-js';
import throttle from 'lodash/throttle';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Image, Platform, StyleSheet, Text, View } from 'react-native';

import {
  useInitiateMyEmailReminderMutation,
  useRespondToMyEmailReminderChallengeMutation,
} from 'apollo/queries/autogenerate/hooks-public';
import {
  PinCodeDispatchMethod,
  Platform as ReCaptchaPlatform,
} from 'apollo/queries/autogenerate/schemas-public';
import IMAGES from 'shared/constants/images';
import { EmailReminderPinCode } from 'shared/modules/auth/components/organisms/PinCode';
import { ACTION, ITEM, SCREEN } from 'shared/modules/auth/constants/mixpanel';
import Button from 'shared/modules/common/components/atoms/Button';
import Dialog from 'shared/modules/common/components/atoms/Dialog';
import TextInput from 'shared/modules/common/components/atoms/TextInput';
import generateID from 'shared/modules/common/utils/generate-id';
import validator from 'shared/utils/validator';
import Mixpanel from 'shared/vendor/Mixpanel';

const OTP_IDLE_TIME_LIMIT = 3 * 60 * 1000;

const VIEW_SUCCESS_PAGE = `${SCREEN.EMAIL_REMINDER_SUCCESS} - ${ACTION.PAGE_VIEW}`;
const VIEW_FORGOT_EMAIL_MULTIPLE_ATTEMPTS_ERROR = `${SCREEN.FORGOT_EMAIL_MULTIPLE_ATTEMPTS_ERROR} - ${ACTION.PAGE_VIEW}`;
const VIEW_FORGOT_EMAIL_TIMEOUT_ERROR = `${SCREEN.FORGOT_EMAIL_TIMEOUT_ERROR} - ${ACTION.PAGE_VIEW}`;
const ERROR_INVALID_OTP = `${SCREEN.FORGOT_EMAIL_INVALID_OTP_ERROR} - ${ACTION.ERROR}`;
const CLICK_EVENT = `${SCREEN.FORGOT_EMAIL_ADDRESS} - ${ACTION.CLICK}`;
const EDIT_FIELD_EVENT = `${SCREEN.FORGOT_EMAIL_ADDRESS} - ${ACTION.FIELD_EDIT}`;

type FormProps = {
  phone: string;
  otp: string;
};

type Props = {
  isVisible: boolean;
  onClose: () => void;
  reCaptchaPlatform: ReCaptchaPlatform;
  getRecaptchaTokenFunction: () => Promise<string>;
  onNavigateBack: () => void;
};

const EmailReminderUnauthenticatedDialog = (props: Props) => {
  const [stepCounter, setStepCounter] = useState(1);
  const [errorViewProps, setErrorViewProps] = useState<{
    title: string;
    body: string;
    isTryAgain: boolean;
  } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>('');
  const [showOTPError, setShowOTPError] = useState(false);

  // Count ellapsed time in OTP screen
  const otpIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // OTP session value generated by BE
  const [otpSession, setOtpSession] = useState<string | null>(null);

  // Reference:
  // https://github.com/paidy/api-consumers-rust/blob/master/docs/flows/emailReminder/initiate.md
  const [initiateMyEmailReminder] = useInitiateMyEmailReminderMutation({
    context: {
      uri: process.env.CONSUMER_API_HOST_GRAPHQL_PUBLIC,
    },
  });

  // Reference:
  // https://github.com/paidy/api-consumers-rust/blob/master/docs/flows/emailReminder/respond_to_challenge.md
  const [
    respondToMyEmailReminderChallenge,
  ] = useRespondToMyEmailReminderChallengeMutation({
    context: {
      uri: process.env.CONSUMER_API_HOST_GRAPHQL_PUBLIC,
    },
  });

  const form = useForm<FormProps>({
    defaultValues: {
      phone: '',
    },
  });

  form.watch();

  useEffect(() => {
    if (stepCounter === 2) {
      restartIdleTimer();
    } else {
      resetIdleTimer();
    }
  }, [stepCounter]);

  useEffect(() => {
    form.register(
      {
        name: 'phone',
      },
      {
        validate: validator.getRules([
          'mustNotBeEmptyOrSpace',
          'mustBeValidPhone',
        ]),
      }
    );
  }, []);

  const resetIdleTimer = () => {
    if (otpIdleTimerRef.current) {
      clearTimeout(otpIdleTimerRef.current);
    }
  };

  const restartIdleTimer = () => {
    resetIdleTimer();

    // Count ellapsed time in OTP screen.
    otpIdleTimerRef.current = setTimeout(() => {
      Mixpanel.trackEvent(VIEW_FORGOT_EMAIL_TIMEOUT_ERROR);
      setStepCounter(4);
      setErrorViewProps({
        title: `もう一度お試しください`,
        body: `制限時間内に入力が完了しませんでした。\nお手数ですが、もう一度はじめから\nお試しください。`,
        isTryAgain: true,
      });
    }, OTP_IDLE_TIME_LIMIT);
  };

  const onFulFill = (code: string) => {
    restartIdleTimer();
    confirmOTP(code);
  };

  const submitInitiateEmailReminder = throttle(
    async (method: PinCodeDispatchMethod) => {
      Mixpanel.trackEvent(CLICK_EVENT, {
        ITEM: ITEM.SUBMIT,
        'Event Type': method,
      });

      const isValid = await form.triggerValidation(['phone']);

      if (isValid) {
        try {
          const reCaptchaToken = await props.getRecaptchaTokenFunction();
          const { phone } = form.getValues();

          setErrorMessage(null);

          try {
            // API requires international standard format
            // e.g. 08000000001 -> +8000000001
            const phoneWithCountryPrefix = parsePhoneNumber(
              phone,
              'JP'
            ).number.toString();
            const result = await initiateMyEmailReminder({
              variables: {
                phoneNumber: phoneWithCountryPrefix,
                pinCodeDispatchMethod: method,
                recaptchaPlatform: props.reCaptchaPlatform,
                recaptchaToken: reCaptchaToken,
              },
            });

            // Expect to get a session value
            if (result.data) {
              setOtpSession(result.data?.initiateMyEmailReminder);
              setStepCounter(2);
            } else {
              setErrorMessage('エラーが発生しました');
            }
            // Failed to initialize reminder flow
          } catch (e: any) {
            // BE also validate phone number format.
            if (e.graphQLErrors?.[0]?.message === 'wrong phone_number') {
              setErrorMessage(
                '携帯電話番号に誤りがあります。11桁で入力してください。'
              );
              // This phone number has already been used in the email reminder flow within 24h
            } else if (e.graphQLErrors?.[0]?.extensions?.code === 403) {
              Mixpanel.trackEvent(VIEW_FORGOT_EMAIL_MULTIPLE_ATTEMPTS_ERROR, {
                ITEM: ITEM.CLOSE_DIALOG,
              });

              setStepCounter(4);
              setErrorViewProps({
                title: `時間をおいてから\n再度お試しください。`,
                body: `メールアドレスの確認は、\n一度行うと24時間以内は行えません。\n時間をおいてから再度お試しください。`,
                isTryAgain: false,
              });
              // Usually something wrong with BE
            } else if (e.graphQLErrors?.[0]?.extensions?.code === 500) {
              setErrorMessage('サーバーエラーが発生しました');
            } else {
              setErrorMessage('エラーが発生しました');
            }
          }
          // Failed to get a recaptcha token.
        } catch (e) {
          setErrorMessage('エラーが発生しました');
        }
      } else {
        setErrorMessage('入力内容を確認してください');
      }
    },
    2000,
    { leading: true, trailing: false }
  );

  const onSMSInvoke = () => {
    submitInitiateEmailReminder(PinCodeDispatchMethod.SMS);
  };

  const onVoiceCallInvoke = () => {
    submitInitiateEmailReminder(PinCodeDispatchMethod.VOICE);
  };

  const confirmOTP = async (code: string) => {
    if (otpSession) {
      try {
        const reCaptchaToken = await props.getRecaptchaTokenFunction();

        setShowOTPError(false);

        try {
          const data = await respondToMyEmailReminderChallenge({
            variables: {
              session: otpSession,
              answer: code,
              recaptchaPlatform: props.reCaptchaPlatform,
              recaptchaToken: reCaptchaToken,
            },
          });
          const newSession = data.data?.respondToMyEmailReminderChallenge;

          // If `newSession` exists, it means the OTP challenge failed.
          // The user may try again using the new session value.
          if (newSession) {
            Mixpanel.trackEvent(ERROR_INVALID_OTP);
            setShowOTPError(true);
            setOtpSession(newSession);
            // Success
          } else {
            Mixpanel.trackEvent(VIEW_SUCCESS_PAGE, {
              Item: ITEM.CLOSE_DIALOG,
            });

            setStepCounter(3);
          }
        } catch (err: any) {
          if (err.graphQLErrors?.[0]?.extensions?.code === 500) {
            setErrorMessage('サーバーエラーが発生しました');

            // OTP challenge failed more than 3 times.
            // The phoneNumber should be locked out for a while.
          } else if (err.graphQLErrors?.[0]?.extensions?.code === 401) {
            setStepCounter(4);
            setErrorViewProps({
              title: `認証エラー`,
              body: `お手数ですが、再度お手続きを\nお願いいたします。`,
              isTryAgain: true,
            });
          } else {
            setErrorMessage('エラーが発生しました');
          }
        }
      } catch (e) {
        setErrorMessage('エラーが発生しました');
      }
    } else {
      setErrorMessage('エラーが発生しました');
    }
  };

  const cleanUpState = () => {
    setStepCounter(1);
    setErrorMessage('');
    setShowOTPError(false);
    setOtpSession(null);
    form.setValue('phone', '');
    form.clearError();
  };

  const onClose = () => {
    cleanUpState();
    props.onClose();
  };

  const onNavigateBack = () => {
    cleanUpState();
    props.onNavigateBack();
  };

  const onBackButtonPress = () => {
    if (stepCounter === 1) {
      onNavigateBack();
    } else {
      setErrorMessage(null);
      setShowOTPError(false);
      setStepCounter(stepCounter - 1);
    }
  };

  const onErrorCloseButtonPress = (isTryAgain: boolean) => {
    if (isTryAgain) {
      setErrorMessage(null);
      setStepCounter(1);
    } else {
      onClose();
    }
  };

  return (
    <Dialog
      title="メールアドレスの確認"
      open={props.isVisible}
      style={style.dialog}
      showBackButton={stepCounter <= 2}
      onBackButtonPress={onBackButtonPress}
      onCloseButtonPress={onClose}
    >
      <View
        {...Platform.select({ web: { accessibilityRole: 'form' } })}
        style={style.container}
      >
        {/* STEP 1 - Input phone number */}
        {stepCounter === 1 && (
          <View
            style={style.container}
            {...generateID('step1-email-reminder-dialog')}
          >
            <View style={style.topSection}>
              <Text style={style.title}>携帯電話番号を入力</Text>
              <Text style={style.body}>
                携帯電話番号を入力して、認証コードを受け取ってください。認証コード入力後、ご登録のメールアドレスがSMSに送信されます。
              </Text>
              <Text style={style.caption}>
                ※セキュリティ上の理由により、メールアドレスは一部を隠した状態でお送りします。
              </Text>
              <TextInput
                label="携帯電話番号"
                placeholder="090 0000 0000"
                value={form.getValues('phone')}
                onChangeText={(text) => form.setValue('phone', text)}
                onFocus={() => {
                  Mixpanel.trackEvent(EDIT_FIELD_EVENT, {
                    Item: ITEM.ENTER_PHONE_NUMBER,
                  });
                }}
                onBlur={() => {
                  setErrorMessage('');
                  form.clearError('phone');
                  form.triggerValidation('phone');
                }}
                message={
                  (form.errors.phone?.message as string) || errorMessage || ''
                }
                state={
                  form.errors.phone?.message || errorMessage
                    ? 'error'
                    : undefined
                }
                id="edit-phone-field"
              />
            </View>
            <Button
              title="認証コードを受け取る"
              onPress={onSMSInvoke}
              style={style.button}
              id="edit-phone-next-button"
            />
          </View>
        )}
        {/* STEP 2 - Phone number OTP */}
        {stepCounter === 2 && (
          <View
            style={style.container}
            {...generateID('step2-email-reminder-dialog')}
          >
            <EmailReminderPinCode
              onFulFill={onFulFill}
              onVoiceCallInvoke={onVoiceCallInvoke}
              showError={showOTPError}
            />
          </View>
        )}
        {/* STEP 3 - SUCCESS */}
        {stepCounter === 3 && (
          <View
            style={style.container}
            {...generateID('step3-email-reminder-dialog')}
          >
            <View style={style.topSectionCenter}>
              <Image
                source={{ uri: IMAGES.success2 }}
                style={style.successImg}
              />
              <Text style={[style.title, style.textCenter]}>
                {`ご登録のメールアドレスを\nSMSに送信しました。`}
              </Text>
            </View>
            <Button
              title={'閉じる'}
              onPress={onClose}
              style={style.button}
              id="close-email-reminder-dialog"
            />
          </View>
        )}
        {stepCounter === 4 && errorViewProps && (
          <View
            style={style.container}
            {...generateID('step4-email-reminder-dialog')}
          >
            <View style={style.topSectionCenter}>
              <View>
                <Text style={[style.title, style.textCenter]}>
                  {errorViewProps.title}
                </Text>
                <Text style={[style.body, style.textCenter]}>
                  {errorViewProps.body}
                </Text>
              </View>
            </View>
            <Button
              title={errorViewProps.isTryAgain ? 'もう一度試す' : '閉じる'}
              onPress={() => onErrorCloseButtonPress(errorViewProps.isTryAgain)}
              style={style.button}
              id="close-email-reminder-dialog"
            />
          </View>
        )}
      </View>
    </Dialog>
  );
};

const style = StyleSheet.create({
  container: {
    flex: 1,
  },
  topSection: {
    flexGrow: 1,
  },
  topSectionCenter: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dialog: {
    width: '100%',
    maxWidth: 424,
    minHeight: 548,
    marginTop: 64,
  },
  title: {
    fontSize: 20,
    lineHeight: 24,
    fontWeight: '600',
    color: '#2a2e32',
    marginBottom: 24,
  },
  body: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: '300',
    color: '#2a2e32',
    marginBottom: 24,
  },
  caption: {
    fontSize: 13,
    lineHeight: 16,
    fontWeight: '300',
    color: '#586065',
    marginBottom: 32,
  },
  textCenter: {
    alignSelf: 'center',
    textAlign: 'center',
  },
  button: {
    maxWidth: 312,
    alignSelf: 'center',
    marginVertical: 8,
  },
  successImg: {
    width: 204,
    aspectRatio: 1,
    marginTop: 32,
    marginBottom: 24,
  },
});

export default EmailReminderUnauthenticatedDialog;
